"use client"

import * as React from "react"
import { ArrowRightIcon, DownloadIcon, EyeOffIcon } from "lucide-react"
import { toast } from "sonner"

import { ReportExportType } from "@ibada/shared-types/reporting"
import { Badge, Button, Skeleton, cn } from "@ibada/ui"

import { useTranslations } from "@/i18n"
import { formatCurrency } from "@/shared/lib/formatting/currency"
import { REPORT_BADGE_LABEL, REPORT_BADGE_VARIANT } from "@/features/reports/components/catalog/report-card"
import {
  KeyFigureState,
  KeyFigureTone,
  KeyReportMetric,
  KeyValueKind,
  MiniChartKind,
  type KeyFigure,
  type KeyFigureContext,
  type KeyFigureValue,
  type MiniChart,
} from "@/features/reports/lib/catalog-key-reports"
import { parseConfigFromParams } from "@/features/reports/lib/report-config-url"
import { reportCatalogGroup, reportCatalogTabLabelKey } from "@/features/reports/model/report-catalog-tab"
import {
  ReportExportKind,
  resolveReportBadge,
  type FinanceReportEntry,
} from "@/features/reports/model/report-registry-types"
import { useFinanceReportScope } from "@/features/reports/services/use-finance-report-scope"
import { useReportData } from "@/features/reports/services/use-report-data"
import { useReportExport } from "@/features/reports/services/use-report-export"

const TONE_CLASS: Record<KeyFigureTone, string> = {
  [KeyFigureTone.Neutral]: "text-foreground",
  [KeyFigureTone.Positive]: "text-teal-700 dark:text-teal-400",
  [KeyFigureTone.Negative]: "text-rose-700 dark:text-rose-400",
}

function formatMoney(amount: string, currency: string, signed: boolean): string {
  const text = formatCurrency(amount, currency)
  return signed && !amount.trim().startsWith("-") && Number(amount) !== 0 ? `+${text}` : text
}

function formatValue(value: KeyFigureValue): string {
  return value.kind === KeyValueKind.Money ? formatMoney(value.amount, value.currency, value.signed) : String(value.count)
}

/**
 * One of the catalog's essentials (R3): what the report is about, its name, today's headline from the boards,
 * a small chart, one muted line of context, and the two things a reader does with it (open it, take the PDF).
 */
export function KeyReportCard({
  entry,
  metric,
  title,
  description,
  figure,
  onOpen,
}: {
  entry: FinanceReportEntry
  metric: KeyReportMetric
  title: string
  description?: string
  figure: KeyFigure
  onOpen: () => void
}) {
  const t = useTranslations()
  const badge = resolveReportBadge(entry)
  const label =
    metric === KeyReportMetric.None
      ? t(reportCatalogTabLabelKey(reportCatalogGroup(entry)) as never)
      : t(`finance.reports.catalog.keys.label.${metric}` as never)

  return (
    <section
      data-testid='key-report-card'
      data-report-id={entry.id}
      data-figure-state={figure.state}
      className='relative flex h-full min-w-0 flex-col gap-2 rounded-2xl border border-border/60 bg-card px-5 py-[18px] transition-colors hover:border-primary/40'
    >
      <span className='truncate text-[12.5px] font-bold text-muted-foreground'>{label}</span>
      <span className='flex min-w-0 items-center gap-1.5'>
        {/* The title opens the report and stretches over the whole card, so the card is the target, not a loud button. */}
        <h3 className='truncate text-base font-bold text-foreground'>
          <button
            type='button'
            onClick={onOpen}
            className='text-left after:absolute after:inset-0 after:rounded-2xl focus-visible:outline-none focus-visible:after:ring-2 focus-visible:after:ring-ring'
          >
            {title}
          </button>
        </h3>
        {badge ? (
          <Badge variant={REPORT_BADGE_VARIANT[badge]} className='shrink-0 px-1.5 py-0 text-[10px]'>
            {t(REPORT_BADGE_LABEL[badge] as never)}
          </Badge>
        ) : null}
      </span>

      <KeyFigureBody figure={figure} description={description} />

      <div className='relative z-10 mt-auto flex items-center gap-2 pt-1'>
        <Button variant='secondary' size='sm' onClick={onOpen} tabIndex={-1}>
          {t("finance.reports.catalog.keys.open" as never)}
          <ArrowRightIcon aria-hidden data-icon='inline-end' />
        </Button>
        <KeyReportPdfButton entry={entry} />
      </div>
    </section>
  )
}

function KeyFigureBody({ figure, description }: { figure: KeyFigure; description?: string }) {
  const t = useTranslations()

  if (figure.state === KeyFigureState.Loading) {
    return (
      <div aria-busy className='flex flex-col gap-2'>
        <div className='flex items-end gap-3'>
          <Skeleton className='h-8 w-32 rounded-lg' />
          <Skeleton className='ml-auto h-11 w-[8.75rem] rounded-lg' />
        </div>
        <Skeleton className='h-3 w-40' />
      </div>
    )
  }

  if (figure.state === KeyFigureState.Withheld) {
    // ADR-0024: the reader may open the report's own refusal, but the catalog never states the amounts.
    return (
      <div data-testid='key-figure-withheld' className='flex flex-col gap-1'>
        <span className='flex items-center gap-1.5 text-sm font-bold text-foreground'>
          <EyeOffIcon aria-hidden className='size-4 text-muted-foreground' />
          {t("finance.reports.catalog.keys.withheld" as never)}
        </span>
        {description ? <p className='line-clamp-2 text-[12.5px] text-muted-foreground'>{description}</p> : null}
      </div>
    )
  }

  if (figure.state === KeyFigureState.None) {
    return description ? <p className='line-clamp-3 text-[13px] text-muted-foreground'>{description}</p> : null
  }

  return (
    <>
      <div className='flex items-end gap-3'>
        <span
          data-testid='key-figure-value'
          className={cn(
            "text-[1.875rem] leading-none font-extrabold tracking-tight whitespace-nowrap tabular-nums",
            TONE_CLASS[figure.tone],
          )}
        >
          {formatValue(figure.value)}
        </span>
        {figure.chart ? <MiniChartView chart={figure.chart} className='ml-auto' /> : null}
      </div>
      {figure.context ? (
        <p data-testid='key-figure-context' className='truncate text-[12.5px] text-muted-foreground'>
          <KeyContextLine context={figure.context} currency={figure.value.kind === KeyValueKind.Money ? figure.value.currency : ""} />
        </p>
      ) : null}
    </>
  )
}

function KeyContextLine({ context, currency }: { context: KeyFigureContext; currency: string }) {
  const t = useTranslations()
  const params: Record<string, string | number> = { ...(context.counts ?? {}) }
  for (const [key, money] of Object.entries(context.money ?? {})) {
    params[key] = formatMoney(money.amount, currency, money.signed)
  }
  return <>{t(`finance.reports.catalog.keys.context.${context.key}` as never, params as never)}</>
}

const CHART_WIDTH = 140
const CHART_HEIGHT = 44

/** The card's small chart: geometry only, decorative (the figure and the line under it carry the meaning). */
export function MiniChartView({ chart, className }: { chart: MiniChart; className?: string }) {
  if (chart.kind === MiniChartKind.Pairs) {
    const max = Math.max(0, ...chart.pairs.flatMap((pair) => [pair.income, pair.expense]))
    const slot = CHART_WIDTH / Math.max(1, chart.pairs.length)
    const bar = slot * 0.32
    const height = (value: number) => (max > 0 ? (Math.max(0, value) / max) * (CHART_HEIGHT - 4) : 0)
    return (
      <svg aria-hidden width={CHART_WIDTH} height={CHART_HEIGHT} className={cn("shrink-0", className)}>
        {chart.pairs.map((pair, index) => (
          <g key={index}>
            <rect
              x={index * slot}
              y={CHART_HEIGHT - height(pair.income)}
              width={bar}
              height={height(pair.income)}
              rx={1.5}
              className='fill-teal-600 dark:fill-teal-400'
            />
            <rect
              x={index * slot + bar + 1}
              y={CHART_HEIGHT - height(pair.expense)}
              width={bar}
              height={height(pair.expense)}
              rx={1.5}
              className='fill-rose-500 dark:fill-rose-400'
            />
          </g>
        ))}
      </svg>
    )
  }

  const max = Math.max(0, ...chart.values.map((value) => Math.abs(value)))
  const slot = CHART_WIDTH / Math.max(1, chart.values.length)
  const bar = slot * 0.62
  return (
    <svg aria-hidden width={CHART_WIDTH} height={CHART_HEIGHT} className={cn("shrink-0", className)}>
      {chart.values.map((value, index) => {
        const height = max > 0 ? Math.max(2, (Math.abs(value) / max) * (CHART_HEIGHT - 4)) : 2
        const last = index === chart.values.length - 1
        return (
          <rect
            key={index}
            x={index * slot}
            y={CHART_HEIGHT - height}
            width={bar}
            height={height}
            rx={2}
            opacity={last ? 1 : 0.45}
            className={value < 0 ? "fill-rose-500 dark:fill-rose-400" : "fill-emerald-600 dark:fill-emerald-500"}
          />
        )
      })}
    </svg>
  )
}

/**
 * The report's existing PDF export, offered only when the report has one for this reader.
 *
 * A template report exports straight from its definition. A report whose PDF is drawn from its own rows (the
 * statements, the accounting tables) needs them first, so its rows are read the way the viewer reads them, with
 * the viewer's default config, and only when the reader asks.
 */
function KeyReportPdfButton({ entry }: { entry: FinanceReportEntry }) {
  const t = useTranslations()
  const { currency } = useFinanceReportScope()
  const config = React.useMemo(() => parseConfigFromParams(new URLSearchParams(), entry.configSchema), [entry.configSchema])
  const exporter = useReportExport(entry, undefined, currency, config)
  const [loadingRows, setLoadingRows] = React.useState(false)

  if (!exporter.formats.includes(ReportExportType.PDF)) return null

  const busy = exporter.isExporting || loadingRows
  const onClick = () => {
    if (entry.export.kind === ReportExportKind.Engine) exporter.exportAs(ReportExportType.PDF)
    else setLoadingRows(true)
  }

  return (
    <>
      <Button variant='ghost' size='sm' onClick={onClick} isLoading={busy} disabled={busy} data-testid='key-report-pdf'>
        {busy ? null : <DownloadIcon aria-hidden />}
        {t("finance.reports.catalog.keys.pdf" as never)}
      </Button>
      {loadingRows ? <RowsPdfExport entry={entry} config={config} onDone={() => setLoadingRows(false)} /> : null}
    </>
  )
}

/** Reads the report's rows, exports them as PDF once, then hands back. Rendered only while that is happening. */
function RowsPdfExport({
  entry,
  config,
  onDone,
}: {
  entry: FinanceReportEntry
  config: ReturnType<typeof parseConfigFromParams>
  onDone: () => void
}) {
  const t = useTranslations()
  const report = useReportData(entry, config)
  const exporter = useReportExport(entry, report.data, report.currency, config)
  const requested = React.useRef(false)
  const sawExporting = React.useRef(false)

  React.useEffect(() => {
    if (requested.current || report.isLoading) return
    requested.current = true
    if (!report.data) {
      toast.error(t("finance.reports.export.failed" as never))
      onDone()
      return
    }
    exporter.exportAs(ReportExportType.PDF)
  }, [report.isLoading, report.data, exporter, onDone, t])

  React.useEffect(() => {
    if (exporter.isExporting) sawExporting.current = true
    else if (sawExporting.current) onDone()
  }, [exporter.isExporting, onDone])

  return null
}
